import {invokeApig} from "../libs/awsLib";
import Paths from "../api/paths";

const ENDPOINT_HASH_KEY = "webPushEndpointHash";

export const isPushSupported = () =>
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window;

function urlBase64ToUint8Array(base64) {
    const padding = "=".repeat((4 - (base64.length % 4)) % 4);
    const raw = atob((base64 + padding).replace(/-/g, "+").replace(/_/g, "/"));
    return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

// kolejnosc ma znaczenie - UA Edge/Opera/Samsung zawiera tez "Chrome", a wszystkie przegladarki na iOS "Safari"
const BROWSERS = [
    [/Edg(e|A|iOS)?\//, "Edge"],
    [/OPR\/|OPiOS\//, "Opera"],
    [/SamsungBrowser\//, "Samsung Internet"],
    [/Firefox\/|FxiOS\//, "Firefox"],
    [/Chrome\/|CriOS\//, "Chrome"],
    [/Safari\//, "Safari"],
];

const OPERATING_SYSTEMS = [
    [/Android/, "Android"],
    [/iPhone|iPod/, "iOS"],
    [/iPad/, "iPadOS"],
    [/Windows/, "Windows"],
    [/CrOS/, "ChromeOS"],
    [/Mac OS X/, "macOS"],
    [/Linux/, "Linux"],
];

const MOBILE_OS = ["Android", "iOS"];
const TABLET_OS = ["iPadOS"];

function getDeviceLabel() {
    const ua = navigator.userAgent;
    const browser =
        BROWSERS.find(([regex]) => regex.test(ua))?.[1] || "Browser";
    let os = OPERATING_SYSTEMS.find(([regex]) => regex.test(ua))?.[1] || "";
    // iPadOS 13+ podaje UA desktopowego Safari na macOS
    if (os === "macOS" && navigator.maxTouchPoints > 1) os = "iPadOS";
    return os ? `${browser} / ${os}` : browser;
}

/** Ikona typu urzadzenia na podstawie deviceLabel zapisanego przez getDeviceLabel. */
export const getDeviceIcon = (deviceLabel = "") => {
    const os = deviceLabel.split(" / ")[1];
    if (MOBILE_OS.includes(os)) return "fa-mobile-screen";
    if (TABLET_OS.includes(os)) return "fa-tablet-screen-button";
    return "fa-desktop";
};

function waitForServiceWorker() {
    return new Promise((resolve, reject) => {
        const timeout = setTimeout(() => {
            reject(new Error("Wait time exceeded 2000ms"));
        }, 2000);
        navigator.serviceWorker.ready.then((registration) => {
            clearTimeout(timeout);
            resolve(registration);
        });
    });
}

async function getVapidKey() {
    const {publicKey} = await invokeApig({...Paths.getPushVapidPublicKey()});
    return urlBase64ToUint8Array(publicKey);
}

/**
 * Zwraca subskrypcje z aktualnym kluczem VAPID. Stara subskrypcja Firebase ma inny klucz
 * i subscribe() rzucilby InvalidStateError, wiec trzeba ja najpierw usunac.
 */
async function getOrCreateSubscription(registration) {
    const key = await getVapidKey();
    const existing = await registration.pushManager.getSubscription();
    if (existing) {
        const current = existing.options.applicationServerKey;
        const currentKey = current ? new Uint8Array(current) : null;
        const isSameKey =
            !!currentKey &&
            currentKey.length === key.length &&
            currentKey.every((b, i) => b === key[i]);
        if (isSameKey) return existing;
        await existing.unsubscribe();
    }
    return registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: key,
    });
}

/** Usuwa periodic sync, ktorym stary SW odswiezal token Firebase. */
async function unregisterLegacyPeriodicSync(registration) {
    try {
        await registration.periodicSync?.unregister("refresh-sns");
    } catch (e) {
        console.error("Error while unregistering periodic sync", e);
    }
}

export const askForPermissionToReceiveNotifications = async () => {
    try {
        if (isPushSupported() && Notification.permission === "default") {
            await Notification.requestPermission();
        }
    } catch (e) {
        console.error(e);
    }
};

/**
 * Zapisuje lub odswieza subskrypcje tego urzadzenia. Wolane przy kazdym wejsciu do aplikacji,
 * odswieza TTL subskrypcji na backendzie.
 */
export const syncPush = async (user) => {
    if (
        !isPushSupported() ||
        Notification.permission !== "granted" ||
        process.env.REACT_APP_STAGE === "local"
    ) {
        return;
    }
    try {
        const registration = await waitForServiceWorker();
        await unregisterLegacyPeriodicSync(registration);
        const subscription = await getOrCreateSubscription(registration);
        const result = await invokeApig({
            ...Paths.subscribePush(user.LocalUserID),
            body: {
                subscription: subscription.toJSON(),
                deviceLabel: getDeviceLabel(),
            },
        });
        localStorage.setItem(ENDPOINT_HASH_KEY, result.endpointHash);
    } catch (e) {
        console.error("Error registering web push", e);
    }
};

/** Wylogowanie - urzadzenie przestaje dostawac powiadomienia tego usera. */
export const disablePush = async (user) => {
    localStorage.removeItem(ENDPOINT_HASH_KEY);
    if (!isPushSupported()) return;
    try {
        const registration = await navigator.serviceWorker.getRegistration();
        const subscription = await registration?.pushManager.getSubscription();
        if (subscription) {
            await invokeApig({
                ...Paths.unsubscribePush(user.LocalUserID),
                body: {endpoint: subscription.endpoint},
            });
            await subscription.unsubscribe();
        }
    } catch (e) {
        console.error("Error unregistering web push", e);
    }
};

export const getPushDevices = (LocalUserID) =>
    invokeApig({...Paths.getPushDevices(LocalUserID)});

export const removePushDevice = (LocalUserID, endpointHash) =>
    invokeApig({
        ...Paths.unsubscribePush(LocalUserID),
        body: {endpointHash},
    });

export const isCurrentPushDevice = (endpointHash) =>
    localStorage.getItem(ENDPOINT_HASH_KEY) === endpointHash;
